import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import React from 'react'
import useLocalizedPath from './useLocalizedPath'
import { seoPages } from '@/content/seo/manifest'
import { SEO_LOCALE } from '@/lib/seo/constants'

export interface Menu {
  url: string,
  name: string,
  children?: Menu[],
}

/**
 * The SEO pages below each category, taken from the generated manifest so the menu and the
 * sitemap cannot disagree about what exists. Nests by url: /dack/pickup holds
 * /dack/pickup/dodge-ram.
 *
 * Built once at import rather than per render. The manifest is a build-time constant and
 * the pages are Swedish-only, so there is nothing here that varies by render or locale —
 * and useMenu is called from the header, the mobile header and the footer, on every page
 * in the app.
 */
const seoChildrenByParent = ((): Map<string, Menu[]> => {
  const byParent = new Map<string, Menu[]>()

  // Deepest first, so a page's own children are already assembled when it is placed.
  ;[...seoPages]
    .sort((a, b) => b.url.split('/').length - a.url.split('/').length)
    .forEach((page) => {
      const parent = page.url.slice(0, page.url.lastIndexOf('/'))
      const children = byParent.get(page.url)
      const entry: Menu = {
        url: page.url,
        name: page.navLabel,
        ...(children?.length ? { children } : {}),
      }
      const siblings = byParent.get(parent)
      if (siblings) siblings.push(entry)
      else byParent.set(parent, [entry])
    })

  return byParent
})()

const useMenu = () => {
  const { t } = useTranslation('common')
  const { locale, defaultLocale } = useRouter()
  const getLocalizedPath = useLocalizedPath()

  // useMemo, not useState in an effect: the menu used to be empty in the server-rendered
  // HTML and only appear after hydration, which hides every link from crawlers. Nothing
  // here is async — t and getLocalizedPath are both synchronous.
  return React.useMemo<Menu[]>(() => {
    // The SEO pages exist in Swedish only and 404 under other locales, so the submenus
    // and the brands entry would be dead links there.
    const hasSeoPages = (locale ?? defaultLocale) === SEO_LOCALE

    const withChildren = (key: string): Menu => {
      const url = getLocalizedPath(key)
      const children = hasSeoPages ? seoChildrenByParent.get(url) ?? [] : []
      return {
        url,
        name: t(`menu.${key}`),
        ...(children.length ? { children } : {}),
      }
    }

    return [
      withChildren('wheels'),
      withChildren('rims'),
      withChildren('tyres'),
      { url: getLocalizedPath('accessories'), name: t('menu.accessories') },
      ...(hasSeoPages ? [withChildren('brands')] : []),
      { url: getLocalizedPath('about'), name: t('menu.about') },
      { url: getLocalizedPath('contact'), name: t('menu.contact') },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, locale, defaultLocale])
}

export default useMenu
